import * as turf from "@turf/turf";
import { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import ReactMapGl, { useMap } from "react-map-gl";
import { useAuthStore } from '..';
import Shape from "../interfaces/Shape";
import GeometryService from '../services/geometryService';
import { compressImage } from '../utils/imageCompression';
import { defaultMapStyle } from './mapConfig';
import PointsOfInterestMapLayer from "./mapLayers/PointsOfInterestMapLayer";
import MapShapeLayer from "./MapShapeLayer";
import styles from './MapThumbnail.module.css';

const MapThumbnail = ({ id, shapes, onBase64ScreenshotIsReady, compressScreenshot, children }: IMapThumbnailProps) => {
    const geometryService = useMemo(() => new GeometryService(), []);
    const authStore = useAuthStore();
    const map = useMap()[id];
    const [isStyleLoaded, setIsStyleLoaded] = useState(false);
    const isScreenshotCapturedRef = useRef(false);
    const bounds = useMemo(() => {
        const features = geometryService.mapIShapesToPolygonFeatures(shapes);
        if (!features.length)
            return null;

        const featureCollection = turf.featureCollection(features);
        const [minX, minY, maxX, maxY] = turf.bbox(featureCollection);
        return [[minX, minY], [maxX, maxY]] as [[number, number], [number, number]];
    }, [shapes]);

    useEffect(() => {
        if (!map) return;

        const onLoad = () => {
            setIsStyleLoaded(true);
        };
        map.on("load", onLoad);

        return () => {
            map.off("load", onLoad);
        };
    }, [map]);

    useEffect(() => {
        if (!map || !isStyleLoaded || !bounds) return;

        const captureScreenshot = async () => {
            if (isScreenshotCapturedRef.current) return;
            isScreenshotCapturedRef.current = true;
            const canvas = map.getCanvas();

            let dataUrl = canvas.toDataURL("image/jpeg");
            if (compressScreenshot) {
                dataUrl = await compressImage(dataUrl, 5);
            }
            onBase64ScreenshotIsReady(dataUrl.split(",")[1]);
        };

        const timeoutId = setTimeout(captureScreenshot, 12000);
        map.once("idle", captureScreenshot);

        return () => {
            clearTimeout(timeoutId);
            map.off("idle", captureScreenshot);
        };
    }, [map, isStyleLoaded, bounds]);

    return (
        <div className={styles.mapThumbnail}>
            <div className={styles.mapThumbnailInner}>
                <ReactMapGl
                    id={id}
                    initialViewState={bounds ? { bounds, fitBoundsOptions: { padding: 300 } } : undefined}
                    mapboxAccessToken={authStore.publicMapboxAccessToken!}
                    mapStyle={authStore.userSettings!.mapboxStyleUrl || defaultMapStyle}
                    preserveDrawingBuffer={true}
                >
                    {children(isStyleLoaded)}
                    <PointsOfInterestMapLayer visible={false} isStyleLoadedOverride={isStyleLoaded} />
                    {isStyleLoaded && <MapShapeLayer id={`${id}_shapes`} shapes={shapes} color="red" />}
                </ReactMapGl>
            </div>
        </div>
    );
};

export default MapThumbnail;

export interface IMapThumbnailProps {
    id: string;
    shapes: Shape[];
    compressScreenshot?: boolean;
    onBase64ScreenshotIsReady: (base64Screenshot: string) => void;
    children: (isStyleLoaded: boolean) => ReactNode;
}
